/**
 * Área que a ficha de um foco não cobre (`popupEvitar`, ver map/popupArea): o
 * círculo de 375 m da conta do uso do solo, onde estão também o miolo dos
 * imóveis do CAR destacados, ou, com o mapa afastado (o círculo vira um
 * ponto), o símbolo do foco com folga. A ficha abre ao lado, ligada ao foco
 * por uma linha, num lugar livre da parte útil do mapa.
 *
 * Só no desktop. No celular não há lado livre (a ficha é quase da largura da
 * tela): ela abre acima do foco, com a seta apontando para ele, e o mapa anda
 * para ela caber, como antes; o foco fica à vista logo abaixo da seta.
 */
import { useMapInstance } from '../map/mapContext';
import { CONSULTA_CELULAR } from '../map/popupArea';
import type { AreaAEvitar } from '../map/popupControl';
import { lonLatToMap } from '../map/projection';

const RAIO_M = 375;
const M_POR_GRAU = 111_320;
/** Raio mínimo na tela (px): o símbolo do foco, o anel do marcador e um respiro. */
export const RAIO_MINIMO_PX = 22;

export function extensaoDoFoco(lat: number, lon: number, resolucao: number): number[] | null {
  const dlat = RAIO_M / M_POR_GRAU;
  const dlon = RAIO_M / (M_POR_GRAU * Math.cos((lat * Math.PI) / 180));
  const pontos = [
    lonLatToMap(lon - dlon, lat),
    lonLatToMap(lon + dlon, lat),
    lonLatToMap(lon, lat - dlat),
    lonLatToMap(lon, lat + dlat),
    lonLatToMap(lon, lat),
  ];
  if (pontos.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) return null;
  const xs = pontos.map((p) => p[0]);
  const ys = pontos.map((p) => p[1]);
  const [cx, cy] = pontos[4]!;
  const minimo = RAIO_MINIMO_PX * resolucao;
  return [
    Math.min(...xs, cx - minimo),
    Math.min(...ys, cy - minimo),
    Math.max(...xs, cx + minimo),
    Math.max(...ys, cy + minimo),
  ];
}

const noCelular = () => typeof window !== 'undefined' && !!window.matchMedia?.(CONSULTA_CELULAR).matches;

export function areaDoFoco(lat: number, lon: number): AreaAEvitar {
  return () => {
    // A janela virou celular com a ficha aberta (girar o tablet): sem área, a ficha só cabe na tela.
    if (noCelular()) return null;
    const res = useMapInstance.getState().map?.getView().getResolution();
    const area = res ? extensaoDoFoco(lat, lon, res) : null;
    const imovel = imovelAEvitar && imovelAEvitar.lat === lat && imovelAEvitar.lon === lon ? imovelAEvitar.extensao : null;
    if (!area || !imovel) return area;
    return [Math.min(area[0]!, imovel[0]!), Math.min(area[1]!, imovel[1]!), Math.max(area[2]!, imovel[2]!), Math.max(area[3]!, imovel[3]!)];
  };
}

/** O imóvel do CAR escolhido na ficha deste foco (o mapa se aproximou dele): a ficha também sai de cima dele. */
let imovelAEvitar: { lat: number; lon: number; extensao: number[] } | null = null;

/** Marca (ou, com null, desmarca) o imóvel que a ficha do foco em `lat`/`lon` também não cobre. */
export function evitarTambemOImovel(lat: number, lon: number, extensao: number[] | null): void {
  imovelAEvitar = extensao ? { lat, lon, extensao } : null;
}

/** A área a evitar da ficha do foco, ou null no celular (ver acima). */
export function areaDoFocoNaTela(lat: number, lon: number): AreaAEvitar | null {
  return noCelular() ? null : areaDoFoco(lat, lon);
}
